import { GhostButton, TimePicker, TimePickerButton } from '@mgh-app/component-library';
import { DateTime } from 'luxon';
import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useClickAway } from 'react-use';
import { selectRoomById } from 'services/RoomService';
import { useAppSelector } from 'store';
import {
  alignItems,
  backgroundColor,
  borderColor,
  borderStyle,
  borderWidth,
  display,
  fontSize,
  fontWeight,
  gap,
  gridTemplateColumns,
  gridTemplateRows,
  justifyContent,
  justifySelf,
  margin,
  padding,
  textAlign,
  textColor,
  twCls,
  width,
} from 'style';
import { ChangeButton } from './ChangeButton';

const extractTime = (scheduleKey?: string) => {
  const parts = scheduleKey?.split(' ');
  const now = DateTime.now();
  return (
    scheduleKey &&
    parts &&
    DateTime.local(now.year, now.month, now.day, Number(parts[1]), Number(parts[0]))
  );
};

export interface ScheduleDetailsProps {
  scheduleKey?: string;
  nextScheduleKey?: string;
  scheduleItem: { setPoint: number };
  onClose: () => void;
  onSave?: (startTime: DateTime, endTime: DateTime, setPoint: number, scheduleKey?: string) => void;
  onRemove?: (scheduleKey?: string) => void;
}

export const ScheduleDetails = ({
  onClose,
  scheduleKey,
  nextScheduleKey,
  scheduleItem: { setPoint: defaultSetPoint },
  onSave,
  onRemove,
}: ScheduleDetailsProps) => {
  const [showStartTimePicker, setShowStartTimePicker] = useState<boolean>(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<DateTime>(
    extractTime(scheduleKey) || DateTime.now().set({ minute: 0 }),
  );
  const [endTime, setEndTime] = useState<DateTime>(
    extractTime(nextScheduleKey) || DateTime.now().plus({ hour: 1 }).set({ minute: 0 }),
  );
  const [setPoint, setSetPoint] = useState<number>(defaultSetPoint);
  const { roomId = '' } = useParams();
  const room = useAppSelector((state) => selectRoomById(state, roomId));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const startTimeRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const endTimeRef = useRef<any>(null);

  useClickAway(startTimeRef, () => setShowStartTimePicker(false));
  useClickAway(endTimeRef, () => setShowEndTimePicker(false));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSetStartTime = (e: any) => {
    const startTime = DateTime.fromISO(e.target.value as string);
    setStartTime(startTime);
    if (startTime >= endTime) {
      setEndTime(startTime);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSetEndTime = (e: any) => {
    const endTime = DateTime.fromISO(e.target.value as string);
    if (endTime.hour === 0 && endTime.minute === 0) {
      setEndTime(endTime.plus({ days: 1 }));
    } else {
      setEndTime(endTime);
    }
  };

  return (
    <div
      className={twCls(
        display('grid'),
        margin('mx-4', 'my-2'),
        textColor('text-grey-900'),
        gridTemplateColumns('grid-cols-1'),
        gridTemplateRows('grid-rows-min-4-fc-min'),
      )}
      style={{ minHeight: '578px' }}
    >
      <GhostButton
        onClick={onClose}
        variant="text"
        className={twCls(fontSize('text-sm'), justifySelf('justify-self-end'))}
      >
        Close
      </GhostButton>
      <div className={twCls(fontSize('text-lg'), textAlign('text-center'))}>{room?.nickName}</div>
      <div className={twCls(margin('mt-6'), fontSize('text-sm'))}>Set Temperature:</div>
      <div
        className={twCls(
          margin('mt-3'),
          fontSize('text-3xl'),
          justifySelf('justify-self-center'),
          display('flex'),
          gap('gap-8'),
        )}
      >
        <ChangeButton variant="UP" onClick={() => setSetPoint((sp) => (sp < 30 ? sp + 0.5 : sp))} />
        <div className={twCls(fontWeight('font-medium'))}>
          <span>{setPoint.toFixed(1)}</span>&nbsp;°C
        </div>
        <ChangeButton
          variant="DOWN"
          onClick={() => setSetPoint((sp) => (sp > 5 ? sp - 0.5 : sp))}
        />
      </div>
      <div
        className={twCls(
          margin('mt-10'),
          display('grid'),
          gridTemplateColumns('grid-cols-1'),
          gridTemplateRows('grid-rows-min-content-4'),
          gap('gap-x-2'),
          gap('gap-y-6'),
          alignItems('items-center'),
          justifyContent('justify-center'),
        )}
      >
        <div
          className={twCls(
            display('grid'),
            gridTemplateColumns('grid-cols-min-content-2'),
            gap('gap-x-2'),
            justifyContent('justify-center'),
          )}
        >
          <div>Start</div>
          <TimePickerButton
            className={twCls(
              width('w-17'),
              !showStartTimePicker ? margin('mb-6') : undefined,
              backgroundColor('bg-grey-300'),
              textColor('text-grey-900'),
              padding('py-0'),
            )}
            onClick={() => {
              setShowStartTimePicker(!showStartTimePicker);
              setShowEndTimePicker(false);
            }}
            hours={startTime.hour}
            minutes={startTime.minute}
          />
        </div>
        {showStartTimePicker && (
          <TimePicker
            ref={startTimeRef}
            className={twCls(
              margin('my-4'),
              borderColor('border-grey-300'),
              borderStyle('border-solid'),
              borderWidth('border-t', 'border-b'),
            )}
            onIonChange={onSetStartTime}
            value={startTime.toISO()}
            minuteValues={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]}
          />
        )}
        <div
          className={twCls(
            display('grid'),
            gridTemplateColumns('grid-cols-min-content-2'),
            gap('gap-x-2'),
            justifyContent('justify-center'),
          )}
        >
          <div>End</div>
          <TimePickerButton
            className={twCls(
              width('w-17'),
              backgroundColor('bg-grey-300'),
              textColor('text-grey-900'),
              padding('py-0'),
            )}
            onClick={() => {
              setShowEndTimePicker(!showEndTimePicker);
              setShowStartTimePicker(false);
            }}
            hours={endTime.hour}
            minutes={endTime.minute}
          />
        </div>
        {showEndTimePicker && (
          <TimePicker
            ref={endTimeRef}
            className={twCls(
              margin('my-4'),
              borderColor('border-grey-300'),
              borderStyle('border-solid'),
              borderWidth('border-t', 'border-b'),
            )}
            onIonChange={onSetEndTime}
            value={endTime.toISO()}
            minuteValues={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]}
          />
        )}
      </div>
      <div className={twCls(display('flex'), justifyContent('justify-between'))}>
        <GhostButton onClick={() => onRemove?.(scheduleKey)}>- Remove</GhostButton>
        <GhostButton onClick={() => onSave?.(startTime, endTime, setPoint, scheduleKey)}>
          Save
        </GhostButton>
      </div>
    </div>
  );
};
